/**
 * Ground surfacing for Animus Quarter — real PBR asphalt, paving stone and
 * concrete over the carriageway, footways and stem, plus canvas-drawn road
 * markings and a zebra crossing at the junction mouth.
 *
 * Split out of districtScene because the street's material work is the one
 * thing that turns three flat grey rectangles into a place with scale and
 * optical flow, and it earns its own file.
 *
 * Two disciplines carried over from the office (scene.ts):
 *
 *  1. Loading is BEST-EFFORT and DEFERRED. The 2 m PBR sets are only ever seen
 *     once the player is already standing in the district, so they have no
 *     business blocking first paint of the building shells. Each surface keeps
 *     its flat grey-box colour until (unless) a map arrives behind the core
 *     asset batch — a failed or slow download can never blank the street.
 *  2. Grazing angles are where a tiled ground texture lives or dies: a road
 *     seen almost edge-on smears to mush without anisotropic filtering, so the
 *     maps are sampled at anisotropy 8.
 *
 * The road markings are a canvas DETAIL map screened over the asphalt — the
 * single strongest "this is a road" cue, and it costs nothing to download.
 */
import * as pc from 'playcanvas'
import { useArenaStore } from '../../store'
import {
  computeSurfaces, type SurfaceKind,
} from './district'

const TEXTURES_BASE = '/assets/textures/'

/** Real-world metres per texture repeat — a 2 m tile reads as human-scale. */
export const GROUND_REPEAT_M = 2

/** Ground sits fractionally below zero so it never z-fights an interior floor. */
// Sits ABOVE the district terrain plane (whose top is ~-0.10) so the two
// coplanar ground layers can't z-fight — that flicker was the "spasm".
const GROUND_Y = 0.0

/** Anisotropic samples — critical for a tiled ground seen at grazing angles. */
const GROUND_ANISOTROPY = 8

interface GroundTexSet {
  dir: string
  color: string
  normal: string
  roughness: string
}

const TEX_ASPHALT: GroundTexSet = {
  dir: 'asphalt_road',
  color: 'Asphalt026A_1K-JPG_Color.jpg',
  normal: 'Asphalt026A_1K-JPG_NormalGL.jpg',
  roughness: 'Asphalt026A_1K-JPG_Roughness.jpg',
}
const TEX_PAVING: GroundTexSet = {
  dir: 'paving_stones',
  color: 'PavingStones131_1K-JPG_Color.jpg',
  normal: 'PavingStones131_1K-JPG_NormalGL.jpg',
  roughness: 'PavingStones131_1K-JPG_Roughness.jpg',
}
const TEX_CONCRETE: GroundTexSet = {
  dir: 'concrete_apron',
  color: 'Concrete034_1K-JPG_Color.jpg',
  normal: 'Concrete034_1K-JPG_NormalGL.jpg',
  roughness: 'Concrete034_1K-JPG_Roughness.jpg',
}

const GROUND_TEX: Record<SurfaceKind, GroundTexSet> = {
  carriageway: TEX_ASPHALT,
  footway: TEX_PAVING,
  apron: TEX_CONCRETE,
}

/**
 * Flat-colour fallbacks. Deliberately the SAME values the grey-box stage used,
 * so the moment before the maps load is identical to the district today and a
 * failed download degrades to exactly the old look rather than to black.
 */
const GROUND_FALLBACK: Record<SurfaceKind, { color: pc.Color; gloss: number }> = {
  carriageway: { color: new pc.Color(0.20, 0.20, 0.22), gloss: 0.35 },
  footway: { color: new pc.Color(0.55, 0.54, 0.52), gloss: 0.25 },
  apron: { color: new pc.Color(0.48, 0.47, 0.46), gloss: 0.25 },
}

/**
 * UV tiling for a ground patch so its texture repeats every GROUND_REPEAT_M
 * metres. A plane primitive carries 0-1 UVs across its whole extent, so the
 * repeat count IS the tiling. Pure and exported so the invariant is testable.
 */
export function groundTiling(width: number, depth: number): [number, number] {
  return [width / GROUND_REPEAT_M, depth / GROUND_REPEAT_M]
}

type AssetLoader = {
  loadFromUrl?: (
    url: string,
    type: string,
    cb: (err: string | null, asset?: pc.Asset) => void,
  ) => void
}

function getAssetLoader(app: pc.Application): AssetLoader | null {
  const assets = (app as { assets?: AssetLoader }).assets
  if (!assets || typeof assets.loadFromUrl !== 'function') return null
  return assets
}

/** Register a cleanup callback on app teardown when the event API exists. */
function onAppDestroy(app: pc.Application, cb: () => void): void {
  const evts = app as unknown as { on?: (ev: string, fn: () => void) => void }
  evts.on?.('destroy', cb)
}

/**
 * Run `fn` once the core (building-shell) asset batch is in — mirrors how
 * scene.ts defers the HDRI. If the store is unavailable (tests, partial mock)
 * the ground simply keeps its flat-colour fallback.
 */
function afterCoreReady(app: pc.Application, fn: () => void): void {
  try {
    if (useArenaStore.getState().assetProgress.coreReady) {
      fn()
      return
    }
    const unsub = useArenaStore.subscribe((state) => {
      if (state.assetProgress.coreReady) {
        unsub()
        fn()
      }
    })
    onAppDestroy(app, unsub)
  } catch {
    /* store unavailable — skip the deferred maps, keep the flat fallback */
  }
}

/**
 * Wire Color / NormalGL / Roughness maps onto a material already set up for the
 * metalness workflow. Graceful: any load failure leaves the flat-colour
 * material untouched. Roughness feeds an INVERTED gloss (ambientCG ships a
 * roughness map, the engine wants gloss).
 */
function applyGroundTextureSet(
  app: pc.Application,
  mat: pc.StandardMaterial,
  set: GroundTexSet,
  tileX: number,
  tileY: number,
): void {
  const assets = getAssetLoader(app)
  if (!assets) return

  const load = (file: string, apply: (tex: pc.Texture) => void): void => {
    try {
      assets.loadFromUrl!(`${TEXTURES_BASE}${set.dir}/${file}`, 'texture', (err, asset) => {
        if (err || !asset?.resource) return
        try {
          const tex = asset.resource as pc.Texture
          tex.addressU = pc.ADDRESS_REPEAT
          tex.addressV = pc.ADDRESS_REPEAT
          tex.anisotropy = GROUND_ANISOTROPY
          apply(tex)
          mat.update()
        } catch { /* keep the flat-colour fallback */ }
      })
    } catch { /* keep the flat-colour fallback */ }
  }

  load(set.color, (tex) => {
    mat.diffuseMap = tex
    mat.diffuseMapTiling = new pc.Vec2(tileX, tileY)
    mat.diffuse = new pc.Color(1, 1, 1) // let the map carry the colour
  })
  load(set.normal, (tex) => {
    mat.normalMap = tex
    mat.normalMapTiling = new pc.Vec2(tileX, tileY)
    mat.bumpiness = 0.8
  })
  load(set.roughness, (tex) => {
    mat.glossMap = tex
    mat.glossMapTiling = new pc.Vec2(tileX, tileY)
    mat.glossInvert = true
    mat.gloss = 1
  })
}

/** A ground material: metalness workflow, metalness 0, flat-colour until maps land. */
function makeGroundMaterial(kind: SurfaceKind): pc.StandardMaterial {
  const fb = GROUND_FALLBACK[kind]
  const m = new pc.StandardMaterial()
  m.diffuse = fb.color
  m.useMetalness = true
  m.metalness = 0
  m.gloss = fb.gloss
  m.update()
  return m
}

/**
 * Canvas-drawn lane markings, tiled ALONG the carriageway (the High Street runs
 * east-west, so a plane's U axis is the road's length). One dash cycle plus the
 * two solid edge lines, screened over the asphalt so the black background
 * leaves the road untouched and only the white paint shows.
 *
 * @param cyclesPerSegment repeat count along a carriageway segment. An integer
 *   makes the pattern reset in phase at every segment seam, so the dashes stay
 *   continuous across the joins between planes.
 */
function roadMarkingTexture(app: pc.Application): pc.Texture {
  const W = 1024, H = 512
  const canvas = document.createElement('canvas')
  canvas.width = W
  canvas.height = H
  const ctx = canvas.getContext('2d')
  const tex = new pc.Texture(app.graphicsDevice, {
    width: W, height: H,
    addressU: pc.ADDRESS_REPEAT,
    addressV: pc.ADDRESS_REPEAT,
    anisotropy: GROUND_ANISOTROPY,
  })
  if (ctx) {
    // Black is transparent under DETAILMODE_SCREEN — it leaves the asphalt as-is.
    ctx.fillStyle = '#000000'
    ctx.fillRect(0, 0, W, H)

    ctx.fillStyle = '#e8e6df' // slightly warm off-white worn road paint
    // Solid edge lines, one near each kerb (V runs across the carriageway).
    const edge = Math.round(H * 0.03)
    ctx.fillRect(0, Math.round(H * 0.05), W, edge)
    ctx.fillRect(0, H - Math.round(H * 0.05) - edge, W, edge)

    // Broken centre line — one dash + gap per canvas tile, dash on the first
    // 45% so it repeats to an even dash/gap once tiled along the road.
    const cy = Math.round(H * 0.485)
    ctx.fillRect(0, cy, Math.round(W * 0.45), Math.round(H * 0.03))

    tex.setSource(canvas)
  }
  return tex
}

/**
 * Zebra crossing at the stem mouth. Bars run ALONG the carriageway (parallel to
 * the traffic on the High Street, i.e. the plane's U/X axis) and repeat across
 * it — what a driver reads as a crossing. Painted on its own plane just above
 * the road with depthWrite off so it lays over the asphalt without z-fighting.
 */
function zebraTexture(app: pc.Application): pc.Texture {
  const W = 512, H = 512
  const canvas = document.createElement('canvas')
  canvas.width = W
  canvas.height = H
  const ctx = canvas.getContext('2d')
  const tex = new pc.Texture(app.graphicsDevice, {
    width: W, height: H,
    addressU: pc.ADDRESS_CLAMP_TO_EDGE,
    addressV: pc.ADDRESS_CLAMP_TO_EDGE,
    anisotropy: GROUND_ANISOTROPY,
  })
  if (ctx) {
    // Transparent everywhere except the bars — the asphalt shows in the gaps.
    ctx.clearRect(0, 0, W, H)
    ctx.fillStyle = 'rgba(232,230,223,1)'
    // Eight bars across the crossing's depth (V) → ~0.47 m bars over 7.5 m.
    const bars = 8
    const cell = H / bars
    const bar = cell * 0.55
    for (let i = 0; i < bars; i++) {
      ctx.fillRect(0, Math.round(i * cell + (cell - bar) / 2), W, Math.round(bar))
    }
    tex.setSource(canvas)
  }
  return tex
}

// The junction mouth the zebra covers: the stem's width across the carriageway.
const ZEBRA_MIN_X = -7
const ZEBRA_MAX_X = 7
const ZEBRA_MIN_Z = -4.3 // CARRIAGEWAY_MIN_Z
const ZEBRA_MAX_Z = 3.2  // CARRIAGEWAY_MAX_Z

function buildZebraCrossing(app: pc.Application, root: pc.Entity): void {
  const mat = new pc.StandardMaterial()
  mat.diffuse = new pc.Color(1, 1, 1)
  const tex = zebraTexture(app)
  mat.diffuseMap = tex
  // The canvas alpha carves the bars out; the gaps let the road through.
  mat.opacityMap = tex
  mat.opacityMapChannel = 'a'
  mat.blendType = pc.BLEND_NORMAL
  mat.depthWrite = false // overlay: never write depth, so it can't z-fight the road
  mat.useMetalness = true
  mat.metalness = 0
  mat.gloss = 0.3
  mat.update()

  const w = ZEBRA_MAX_X - ZEBRA_MIN_X
  const d = ZEBRA_MAX_Z - ZEBRA_MIN_Z
  const e = new pc.Entity('zebra-crossing')
  e.addComponent('render', { type: 'plane' })
  if (e.render) {
    for (const mi of e.render.meshInstances) mi.material = mat
    e.render.castShadows = false
  }
  e.setLocalScale(w, 1, d)
  e.setPosition((ZEBRA_MIN_X + ZEBRA_MAX_X) / 2, 0.005, (ZEBRA_MIN_Z + ZEBRA_MAX_Z) / 2)
  root.addChild(e)
}

/**
 * Build every ground patch as a textured plane, sharing one PBR material per
 * (kind, tiling) so the whole district costs at most four ground materials.
 * Carriageway materials additionally carry the screened road-markings detail
 * map. All the JPG map loading is deferred behind the core asset batch.
 */
export function buildGroundSurfaces(app: pc.Application, root: pc.Entity): void {
  const cache = new Map<string, pc.StandardMaterial>()

  const materialFor = (kind: SurfaceKind, w: number, d: number): pc.StandardMaterial => {
    const [tileX, tileY] = groundTiling(w, d)
    const key = `${kind}|${tileX.toFixed(3)}|${tileY.toFixed(3)}`
    const existing = cache.get(key)
    if (existing) return existing

    const mat = makeGroundMaterial(kind)
    cache.set(key, mat)
    afterCoreReady(app, () => applyGroundTextureSet(app, mat, GROUND_TEX[kind], tileX, tileY))

    if (kind === 'carriageway') {
      // Detail map tiled along the road length (U/X). An integer repeat keeps
      // the dashes in phase across the seams between carriageway segments; V=1
      // is one cross-section, so the edge lines land at both kerbs.
      const cyclesAlongLength = Math.max(1, Math.round(w / 4))
      mat.diffuseDetailMap = roadMarkingTexture(app)
      mat.diffuseDetailMode = pc.DETAILMODE_SCREEN
      mat.diffuseDetailMapTiling = new pc.Vec2(cyclesAlongLength, 1)
      mat.update()
    }
    return mat
  }

  for (const patch of computeSurfaces()) {
    const w = patch.rect.maxX - patch.rect.minX
    const d = patch.rect.maxZ - patch.rect.minZ
    const mat = materialFor(patch.kind, w, d)

    // A plane primitive (UVs 0-1, faces +Y) is the right ground primitive: half
    // the faces of a box and a clean 2 m tile.
    const e = new pc.Entity(`surface-${patch.id}`)
    e.addComponent('render', { type: 'plane' })
    if (e.render) {
      for (const mi of e.render.meshInstances) mi.material = mat
      e.render.castShadows = false
    }
    e.setLocalScale(w, 1, d)
    e.setPosition(
      (patch.rect.minX + patch.rect.maxX) / 2,
      GROUND_Y,
      (patch.rect.minZ + patch.rect.maxZ) / 2,
    )
    root.addChild(e)
  }

  buildZebraCrossing(app, root)
}
